// Shared Manager Sub-mode B guidance chat state — DESIGN §5.6.2, §10.1.2 #2.
// One history/mutation implementation for every Manager guidance chat
// surface (the card-based `ManagerMaeveGuidance`, the full-screen
// conversational `ManagerMaeveChat`) — previously hand-rolled per component.
//
// Server is stateless; history is POSTed in full on each turn.
//
// WHERE STATE LIVES, AND WHY NOT `useState` (2026-07-31). The transcript, the
// unsent draft, and the "have we seeded the prefill yet" flag are all held in
// the TanStack query cache. It still dies with the tab — the cache is
// in-memory and `useSignOut` calls `queryClient.clear()` — so the "Manager
// mode is a workplace advisory tool, not a persistent companion" model is
// unchanged. What changes is that an UNMOUNT no longer destroys it, and
// remounts are neither rare nor under this hook's control:
//
//   * `ManagerShell` swaps the bottom-nav app shell for the desktop chrome at
//     the `md` breakpoint. Both render `<Outlet/>`, but they're different
//     component trees, so crossing `md` tears down the whole page. Rotating an
//     iPhone 14 Pro Max to landscape (932px wide) crosses it.
//   * Callers `key={caseId}` per the contract below, so a case switch
//     remounts by design.
//
// `draft` is cached for the same reason as the transcript: losing a
// half-written question to a screen rotation is the same bug as losing the
// answer to it, just quieter.
//
// The transcript write happens inside `mutationFn`, NOT `onSuccess`. TanStack
// removes a mutation observer when its component unmounts and does not fire
// component-level callbacks afterwards — so if the manager rotated, navigated,
// or hit Back while a turn was in flight, `onSuccess` never ran and Maeve's
// reply was silently discarded. `mutationFn` runs to completion regardless.
//
// ---------------------------------------------------------------------------
// CACHE KEY: (caseId, threadId). BOTH parts are load-bearing.
// ---------------------------------------------------------------------------
// `caseId` alone is NOT enough. Three different surfaces open a guidance chat
// about the SAME case, and they are different conversations doing different
// jobs:
//
//   * `case-detail`  — "ask Maeve about this case" on `/manager/cases/:id`
//   * `check-in`     — the Journey 06 welcome-back drafter, which seeds its
//                      own opener via `prefill`
//   * `ask-maeve`    — the `/manager/maeve` thread
//
// Keyed on `caseId` alone they would share one transcript, so opening the
// check-in drafter would show an unrelated policy Q&A from the case-detail
// card AND prepend those turns to the `messages` payload POSTed to the
// CGC-audited `/api/cases/:id/maeve/guidance` endpoint — changing the context
// Maeve answers from, on an audited surface, with nothing on screen to explain
// why. `threadId` is required (no default) so a new surface has to decide
// which conversation it is joining rather than silently joining one.
//
// CONTRACT: this hook does NOT reset when `caseId` changes on an already-
// mounted caller. If your component can be re-rendered with a different
// `caseId` without unmounting (e.g. it's on a `cases/:id` route — React Router
// does NOT unmount on a param-only navigation, so browser back/forward between
// two cases hits this exactly), force a remount with `key={caseId}`. Every
// current caller does. With state now keyed by `(caseId, threadId)` the key is
// belt-and-braces rather than the only guard, but keep it: it also resets the
// DOM (focus, scroll position, textarea selection) which the cache does not.

import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GuidanceTurn } from '@besideu/shared/maeve';
import { api, ApiError } from '@/lib/api.ts';

interface GuidanceResponse {
  message: string;
  promptVersionId: string;
}

/**
 * A turn plus the wall-clock moment it appeared. Display-only: the server
 * doesn't return a timestamp (`GuidanceTurn` is `{role, text}`), so this is
 * "when did this land on screen", NOT an audit timestamp — the audit record
 * is server-side in `maeve_interactions`.
 */
type StoredTurn = GuidanceTurn & { at: number };

interface GuidanceThread {
  turns: StoredTurn[];
  draft: string;
  /**
   * The `prefill` value already seeded into `draft`, or null. Seeding is
   * once-per-value, not once-per-mount: a remount must NOT re-inject an opener
   * the manager has already sent, edited, or deliberately cleared — but a
   * genuinely new `prefill` (a different touch point) should still seed.
   */
  seededPrefill: string | null;
}

const EMPTY_THREAD: GuidanceThread = { turns: [], draft: '', seededPrefill: null };

/** Which conversation this is — see the CACHE KEY note in the file header. */
export type GuidanceThreadId = 'case-detail' | 'check-in' | 'ask-maeve';

function guidanceKey(caseId: string, threadId: GuidanceThreadId) {
  return ['manager', 'guidance', caseId, threadId] as const;
}

export function useManagerGuidanceChat(
  caseId: string,
  threadId: GuidanceThreadId,
  prefill?: string,
) {
  const queryClient = useQueryClient();
  const key = guidanceKey(caseId, threadId);

  // The query cache as a client-state store: `queryFn` seeds an empty thread
  // once and never runs again for this key. Every refetch trigger is off and
  // both times are `Infinity` — a refetch would resolve to `EMPTY_THREAD` and
  // wipe the conversation, which is precisely what this indirection prevents.
  const { data: thread = EMPTY_THREAD } = useQuery<GuidanceThread>({
    queryKey: key,
    queryFn: () => EMPTY_THREAD,
    enabled: caseId !== '',
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  function patch(next: (cur: GuidanceThread) => GuidanceThread) {
    queryClient.setQueryData<GuidanceThread>(key, (cur = EMPTY_THREAD) => next(cur));
  }

  // Seed the opener in an effect, not during render: `setQueryData` notifies
  // every observer of this key synchronously, so a render-phase write can
  // update a DIFFERENT subscribed component mid-render, which React warns
  // about. Guarded on `seededPrefill` so it fires at most once per distinct
  // `prefill` value — a remount must not re-inject an opener the manager
  // already sent or cleared, but a genuinely new one still seeds. Gated ONLY
  // on the value: an earlier version also bailed on a non-empty transcript,
  // which would have silently swallowed a second, different opener mid-thread
  // (latent here — today's only caller passes a per-case constant — but it
  // contradicted the documented contract, and `use-companion-chat.ts` had the
  // same guard where it was reachable).
  useEffect(() => {
    if (!prefill || thread.seededPrefill === prefill) return;
    patch((cur) => ({ ...cur, draft: prefill, seededPrefill: prefill }));
  }, [prefill, thread.seededPrefill]);

  const history = useMemo<GuidanceTurn[]>(
    () => thread.turns.map(({ role, text }) => ({ role, text })),
    [thread.turns],
  );
  const sentAt = useMemo(() => thread.turns.map((t) => t.at), [thread.turns]);

  const ask = useMutation({
    mutationFn: async (text: string): Promise<void> => {
      const userTurn: GuidanceTurn = { role: 'user', text };
      // Read the transcript at send time from the cache rather than closing
      // over `history` — the closure would be stale if two turns were ever in
      // flight, and the cache is the single source of truth either way.
      const prior = queryClient.getQueryData<GuidanceThread>(key)?.turns ?? [];
      const resp = await api.post<GuidanceResponse>(`/api/cases/${caseId}/maeve/guidance`, {
        messages: [...prior.map(({ role, text: t }) => ({ role, text: t })), userTurn],
      });
      const at = Date.now();
      // Append inside `mutationFn` — see the file header. Re-read rather than
      // reusing `prior` so a concurrent append can't be clobbered.
      patch((cur) => ({
        ...cur,
        turns: [...cur.turns, { ...userTurn, at }, { role: 'assistant', text: resp.message, at }],
        // Clear ONLY the draft we just sent. An unconditional `draft: ''` also
        // wiped anything written during the round trip — the manager typing
        // their next question while waiting, or `useSeedGuidanceDraft` landing
        // mid-flight — with no trace of where it went.
        draft: cur.draft.trim() === text ? '' : cur.draft,
      }));
    },
  });

  // Shared submit guard (trim / in-flight check) — both `ManagerMaeveGuidance`
  // (the card) and `ManagerMaeveChat` (the full-screen chat) had hand-rolled an
  // identical copy of this before it moved here.
  function submit() {
    const trimmed = thread.draft.trim();
    if (trimmed.length === 0 || ask.isPending) return;
    ask.mutate(trimmed);
  }

  return {
    history,
    sentAt,
    ask,
    draft: thread.draft,
    setDraft: (v: string) => patch((cur) => ({ ...cur, draft: v })),
    submit,
  };
}

/** What `useSeedGuidanceDraft` actually did — the caller needs to know, since
 *  the two outcomes want different things said to the user. */
export type SeedDraftResult = 'seeded' | 'kept_existing';

/**
 * Writes a question into a guidance thread's unsent draft from OUTSIDE that
 * thread's chat component — for a "start from this question" affordance
 * elsewhere on the page (today: the Significant dates panel's
 * "What should I do about this?" button on the Manager case detail).
 *
 * Why not the `prefill` prop `useManagerGuidanceChat` already takes: that seeds
 * once per distinct VALUE and never again (see `seededPrefill`), which is
 * correct for an opener a surface is born with, but makes a *button* silently
 * do nothing the second time it is pressed.
 *
 * NEVER overwrites a draft the manager is part-way through writing — it returns
 * `'kept_existing'` instead. A seeded question is a convenience; a half-written
 * one is the manager's own words, and there is no undo on a textarea. (The
 * caller announces which happened; silently doing nothing would be its own
 * bug.) An empty-or-whitespace draft, including a previously-seeded question
 * left untouched, is fair game — so re-pressing the button after reading and
 * abandoning it still works.
 *
 * That same rule defers a seed made while a turn is IN FLIGHT: the composer
 * still holds the question being sent (the only trace of it until the reply
 * lands, since turns are appended on response, not optimistically) and is
 * disabled, so overwriting would put a new question somewhere the manager
 * can't edit it. The completing turn clears only the draft it sent, so the box
 * is empty and a second press seeds normally.
 *
 * It seeds the draft and stops there — it never sends. The manager reads the
 * question, edits it if they want, and presses Ask; nothing reaches the
 * CGC-audited endpoint that they did not choose to send.
 */
export function useSeedGuidanceDraft(caseId: string, threadId: GuidanceThreadId) {
  const queryClient = useQueryClient();
  return (text: string): SeedDraftResult => {
    // Same `(caseId, threadId)` key builder the hook itself uses — see the
    // CACHE KEY note in the file header. A second copy of the key shape here
    // would be a silent no-op the day either part changes.
    const cacheKey = guidanceKey(caseId, threadId);
    const current = queryClient.getQueryData<GuidanceThread>(cacheKey)?.draft ?? '';
    if (current.trim().length > 0 && current !== text) return 'kept_existing';
    queryClient.setQueryData<GuidanceThread>(cacheKey, (cur = EMPTY_THREAD) => ({
      ...cur,
      draft: text,
    }));
    return 'seeded';
  };
}

export function explainGuidanceError(error: unknown): { headline: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.status === 503 && error.message === 'maeve_unavailable') {
      return {
        headline: "Maeve isn't available right now",
        detail:
          'A besideU administrator needs to enable the Manager prompt before Maeve can answer questions. Please contact your HR team.',
      };
    }
    if (error.status === 502 && error.message === 'maeve_upstream_error') {
      return {
        headline: "Couldn't reach Maeve",
        detail:
          'The guidance service is temporarily unavailable. Your question wasn’t sent — please try again in a moment.',
      };
    }
  }
  return {
    headline: 'Something went wrong',
    detail: 'Please try again, or refresh the page if the problem persists.',
  };
}
